import { FormElementKind, type DropdownFormElement, type FormElement } from '@gearbelt/global';

import { ApiService } from '@/services/api-v2';

type FilterOption = { label: string; value: string };
type FilterValue = string | string[] | undefined;

const filterByInput = (options: FilterOption[], inputValue?: string): FilterOption[] =>
  inputValue ? options.filter((option) => option.label.toLowerCase().includes(inputValue.toLowerCase())) : options;

const toOptions = (values: readonly string[]): FilterOption[] => values.map((value) => ({ label: value, value }));

// Keep an already-selected filter value visible even if it's no longer among the options (e.g. a list item
// deleted after the filter was set). A Dropdown's stored value is its own label, so the fallback needs no fetch.
const withSelectedValues = (options: FilterOption[], value: FilterValue): FilterOption[] => {
  const selected = Array.isArray(value) ? value : value ? [value] : [];
  const known = new Set(options.map((option) => option.value));
  const missing = selected.filter((entry) => !known.has(entry)).map((entry) => ({ label: entry, value: entry }));
  return missing.length > 0 ? [...options, ...missing] : options;
};

/**
 * Filter options for a Dropdown register column: a static dropdown's options are inline, a list-sourced
 * one fetches its items from the list it draws from. An already-selected value missing from the current
 * options is kept so the active filter still renders.
 */
export function getDropdownFilterOptionsFn(config: DropdownFormElement['config']): (inputValue?: string, value?: FilterValue) => Promise<FilterOption[]> {
  if (config.source === 'static') {
    const options = toOptions(config.options);
    return async (inputValue?: string, value?: FilterValue) => filterByInput(withSelectedValues(options, value), inputValue);
  }

  const { listId } = config;
  // Fetch the list once per column and filter the cached items client-side, so typing doesn't refetch on
  // every keystroke; reset on failure so a later keystroke can retry. A deleted list returns `list: undefined`
  // at HTTP 200, so fall back to no options rather than throwing.
  let optionsPromise: Promise<FilterOption[]> | undefined;
  return async (inputValue?: string, value?: FilterValue) => {
    optionsPromise ??= ApiService.GetListById(listId)
      .then(async (response) => toOptions((await response.json()).list?.elements ?? []))
      .catch((error) => {
        optionsPromise = undefined;
        throw error;
      });
    return filterByInput(withSelectedValues(await optionsPromise, value), inputValue);
  };
}

/**
 * The `getFilterOptionsFn` for a register column: a Dropdown fetches/derives its own options, any other kind
 * with precomputed options serves those, everything else has none.
 */
export function getColumnFilterOptionsFn(
  element: FormElement,
  filterOptions?: FilterOption[],
): ((inputValue?: string, value?: FilterValue) => Promise<FilterOption[]>) | undefined {
  if (element.kind === FormElementKind.Dropdown) {
    return getDropdownFilterOptionsFn(element.config);
  }
  if (filterOptions) {
    return async () => filterOptions;
  }
  return undefined;
}
